import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { displayLabel } from "../../utils/displayLabel";

function normalizeSelectOptions(list = [], isEmployee = false) {
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      if (item == null) return null;
      // Already a react-select option
      if (
        typeof item === "object" &&
        ("value" in item || "label" in item) &&
        !("empCode" in item) &&
        !("user" in item)
      ) {
        return {
          ...item,
          value: item.value != null && typeof item.value === "object" ? item.value.id ?? item.value : item.value,
          label: displayLabel(item.label, displayLabel(item.value, "")),
        };
      }
      const value = item.id ?? item.value ?? item;
      const label = isEmployee
        ? [
            displayLabel(item.empCode || item.employeeCode || item.code, ""),
            displayLabel(
              item.user
                ? [item.user?.name, item.user?.lastName].filter(Boolean).join(" ")
                : item.name || item.fullName,
              ""
            ),
          ]
            .filter(Boolean)
            .join(" - ")
        : displayLabel(item.name ?? item.label ?? item.title ?? item, "");
      return {
        value: typeof value === "object" ? value?.id ?? value : value,
        label: label || String(value ?? ""),
      };
    })
    .filter((o) => o && o.label !== "");
}

const SearchableSelect = ({
  id,
  label,
  value,
  onChange,
  fetchOptions,
  darkMode,
  staticOptions,
  required = false,
  isEmployee = false,
  error,
  disabled = false,
  filterOption,
  openMenuOnlyWhenTyping = false,
  compact = false,
  placeholder: placeholderProp,
  menuPortalTarget,
  menuPlacement = "auto",
  inActionCard = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [menuInputValue, setMenuInputValue] = useState("");

  useEffect(() => {
    const loadOptions = async () => {
      if (staticOptions) {
        setFetchError(null);
        setOptions(normalizeSelectOptions(staticOptions, isEmployee));
        return;
      }

      if (fetchOptions) {
        setLoading(true);
        try {
          const response = await fetchOptions();
          const data = response.data;
          setFetchError(null);
          setOptions(normalizeSelectOptions(data, isEmployee));
        } catch (err) {
          console.error(`Failed to fetch ${label}:`, err);
          setFetchError(`Failed to load ${label.toLowerCase()}. Please try again.`);
        } finally {
          setLoading(false);
        }
      }
    };
    loadOptions();
  }, [fetchOptions, label, staticOptions, isEmployee]);

  const safeOptions = useMemo(
    () => normalizeSelectOptions(options, false),
    [options]
  );

  const resolvedValue =
    safeOptions.find((option) => String(option.value) === String(value ?? "")) ||
    null;

  const placeholder =
    placeholderProp ??
    (label === "Relationship" ||
    label === "Sub Department" ||
    label === "Reporting Manager"
      ? label
      : `Select ${label}`);

  const portalTarget =
    menuPortalTarget === undefined
      ? typeof document !== "undefined"
        ? document.body
        : null
      : menuPortalTarget;

  const actionMenuMax = 168;

  return (
    <div className={`relative ${compact ? "mb-0" : "mb-4"}`}>
      <Select
        id={id}
        name={id}
        value={resolvedValue}
        onChange={(selectedOption) => {
          if (openMenuOnlyWhenTyping) setMenuInputValue("");
          onChange({
            target: {
              name: id,
              value: selectedOption ? selectedOption.value : "",
            },
          });
        }}
        onInputChange={(newValue) => {
          if (openMenuOnlyWhenTyping) setMenuInputValue(newValue || "");
        }}
        inputValue={openMenuOnlyWhenTyping ? menuInputValue : undefined}
        options={safeOptions}
        filterOption={filterOption}
        placeholder={placeholder}
        isSearchable
        isLoading={loading}
        isDisabled={loading || !!fetchError || disabled}
        openMenuOnFocus={!openMenuOnlyWhenTyping}
        openMenuOnClick={!openMenuOnlyWhenTyping}
        menuIsOpen={
          openMenuOnlyWhenTyping ? menuInputValue.length > 0 : undefined
        }
        menuPlacement={menuPlacement}
        menuPortalTarget={portalTarget}
        menuPosition={portalTarget ? "fixed" : "absolute"}
        classNamePrefix="react-select"
        className={`text-sm ${error ? "border-red-500" : ""}`}
        styles={{
          control: (provided, state) => ({
            ...provided,
            borderWidth: 0,
            borderBottomWidth: "1px",
            borderStyle: "solid",
            borderColor: error
              ? "#ef4444"
              : state.isFocused
                ? "hsl(var(--ds-primary))"
                : "hsl(var(--ds-border))",
            borderRadius: 0,
            padding: compact ? "0.15rem 0.25rem" : "0.55rem 0.35rem",
            minHeight: compact ? "34px" : "3.25rem",
            boxShadow: "none",
            backgroundColor: darkMode
              ? "hsl(var(--ds-muted))"
              : "hsl(var(--ds-surface))",
            cursor: disabled ? "not-allowed" : "pointer",
            "&:hover": {
              borderWidth: 0,
              borderBottomWidth: "1px",
              borderColor: error ? "#ef4444" : "hsl(var(--ds-primary-hover))",
              boxShadow: "none",
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: "0 0.35rem",
          }),
          indicatorSeparator: () => ({
            display: "none",
          }),
          // Keep DropdownIndicator mounted; hide the chevron visually.
          dropdownIndicator: (provided) => ({
            ...provided,
            display: "none",
            padding: 0,
            width: 0,
            overflow: "hidden",
            color: "hsl(var(--ds-muted-fg))",
          }),
          clearIndicator: (provided) => ({
            ...provided,
            padding: "0 0.25rem",
          }),
          menu: (provided) => ({
            ...provided,
            zIndex: portalTarget ? 40 : inActionCard ? 25 : 20,
            borderRadius: "var(--ds-radius-lg)",
            overflow: "hidden",
            boxShadow: "var(--ds-shadow-panel)",
            border: inActionCard
              ? "1px solid hsl(var(--ds-primary)/0.28)"
              : "1px solid hsl(var(--ds-border))",
            backgroundColor: "hsl(var(--ds-surface))",
            marginTop: inActionCard ? 4 : provided.marginTop,
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 10050,
          }),
          menuList: (provided) => ({
            ...provided,
            maxHeight: inActionCard
              ? actionMenuMax
              : portalTarget
                ? 220
                : 180,
          }),
          option: (provided, state) => ({
            ...provided,
            borderRadius: 0,
            backgroundColor: state.isSelected
              ? "hsl(var(--ds-primary))"
              : state.isFocused
                ? "hsl(var(--ds-muted))"
                : "hsl(var(--ds-surface))",
            color: state.isSelected
              ? "hsl(var(--ds-primary-fg))"
              : "hsl(var(--ds-fg))",
          }),
          singleValue: (provided) => ({
            ...provided,
            color: "hsl(var(--ds-fg))",
          }),
          placeholder: (provided) => ({
            ...provided,
            color: error ? "#ef4444" : "hsl(var(--ds-muted-fg))",
          }),
        }}
      />
      <label
        htmlFor={id}
        className={`absolute whitespace-nowrap text-sm ${
          error ? "text-red-500" : "text-[hsl(var(--ds-fg))]"
        } duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] ${
          darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-surface))]"
        } px-2 left-1`}
      >
        {label}
        {required && <span className="text-red-500"> *</span>}
      </label>
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {fetchError && <p className="mt-1 text-xs text-red-500">{fetchError}</p>}
    </div>
  );
};

export default SearchableSelect;
